import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CalendarIcon, DollarSignIcon, CheckIcon, XIcon, CircleIcon, MenuIcon, XIcon as CloseIcon } from 'lucide-react';
import bouncedadsLogo from '../images/bouncedads2.png';
import { getNavbarTheme, mergeClasses } from '../utils/themeUtils';
import { getContent } from '../config/content';
import { theme } from '../config/theme';

const Navbar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navbarTheme = getNavbarTheme();
  
  const isActive = (path: string) => {
    if (path === '/home') {
      return location.pathname === '/home' || location.pathname === '/';
    }
    return location.pathname === path;
  };

  const handleFaqClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname === '/contact') {
      // If already on contact page, scroll to FAQ section
      const faqSection = document.getElementById('faq');
      if (faqSection) {
        faqSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      // Navigate to contact page with FAQ hash
      navigate('/contact#faq');
    }
    // Close mobile menu if open
    setMobileMenuOpen(false);
  };

  return (
    <nav className="shadow-lg" style={{ backgroundColor: theme.colors.primary[800], color: theme.colors.text.inverse }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src={bouncedadsLogo} 
                alt={getContent('navigation.logo.alt')} 
                className={navbarTheme.logoSize}
              />
              <span className="font-bold text-xl hidden sm:block">{getContent('company.name')}</span>
              <span className="font-bold text-lg sm:hidden">{getContent('company.name').split(' ')[0]}</span>
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/home"
              className="px-3 py-2 rounded-md text-sm font-medium transition-colors"
              style={isActive('/home') 
                ? { backgroundColor: theme.colors.primary[900], color: 'white' }
                : { color: theme.colors.primary[100] }}
            >
              {getContent('navigation.links.home')}
            </Link>
            <Link
              to="/rentals"
              className="px-3 py-2 rounded-md text-sm font-medium transition-colors"
              style={isActive('/rentals') 
                ? { backgroundColor: theme.colors.primary[900], color: 'white' }
                : { color: theme.colors.primary[100] }}
            >
              {getContent('navigation.links.rentals')}
            </Link>
            <a
              href="/contact#faq"
              onClick={handleFaqClick}
              className="px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer"
              style={isActive('/contact') 
                ? { backgroundColor: theme.colors.primary[900], color: 'white' }
                : { color: theme.colors.primary[100] }}
            >
              {getContent('navigation.links.faq')}
            </a>
            <Link
              to="/contact"
              className="px-3 py-2 rounded-md text-sm font-medium transition-colors"
              style={isActive('/contact') 
                ? { backgroundColor: theme.colors.primary[900], color: 'white' }
                : { color: theme.colors.primary[100] }}
            >
              {getContent('navigation.links.contact')}
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-md text-sm font-medium transition-colors"
              style={{ color: theme.colors.primary[100] }}
            >
              {mobileMenuOpen ? <CloseIcon className="h-6 w-6" /> : <MenuIcon className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <Link
                to="/home"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-md text-base font-medium transition-colors"
                style={isActive('/home') 
                  ? { backgroundColor: theme.colors.primary[900], color: 'white' }
                  : { color: theme.colors.primary[100] }}
              >
                {getContent('navigation.links.home')}
              </Link>
              <Link
                to="/rentals"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-md text-base font-medium transition-colors"
                style={isActive('/rentals') 
                  ? { backgroundColor: theme.colors.primary[900], color: 'white' }
                  : { color: theme.colors.primary[100] }}
              >
                {getContent('navigation.links.rentals')}
              </Link>
              <a
                href="/contact#faq"
                onClick={handleFaqClick}
                className="block px-3 py-2 rounded-md text-base font-medium transition-colors cursor-pointer"
                style={isActive('/contact') 
                  ? { backgroundColor: theme.colors.primary[900], color: 'white' }
                  : { color: theme.colors.primary[100] }}
              >
                {getContent('navigation.links.faq')}
              </a>
              <Link
                to="/contact"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-md text-base font-medium transition-colors"
                style={isActive('/contact') 
                  ? { backgroundColor: theme.colors.primary[900], color: 'white' }
                  : { color: theme.colors.primary[100] }}
              >
                {getContent('navigation.links.contact')}
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
